/**
 * Stats Overview Component
 * Displays 4 key metrics: Users, Sessions, Bookings, Revenue
 */

import { TrendingUp, TrendingDown, Users, MousePointer, Calendar, DollarSign } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { OverviewStats } from '@/hooks/useAnalytics'

interface StatsOverviewProps {
  stats: OverviewStats | null | undefined
  isLoading: boolean
}

interface StatCardProps {
  title: string
  value: string
  change?: number
  icon: React.ReactNode
  isLoading: boolean
}

function StatCard({ title, value, change, icon, isLoading }: StatCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-20 mb-1" />
          <Skeleton className="h-3 w-16" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {change !== undefined && (
          <p
            className={`text-xs flex items-center gap-1 ${
              change >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {change >= 0 ? (
              <TrendingUp className="h-3 w-3" />
            ) : (
              <TrendingDown className="h-3 w-3" />
            )}
            {change >= 0 ? '+' : ''}
            {change.toFixed(1)}% from previous period
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

function formatCurrency(num: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num)
}

export function StatsOverview({ stats, isLoading }: StatsOverviewProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title="Unique Visitors"
        value={stats ? formatNumber(stats.uniqueVisitors) : '-'}
        change={stats?.visitorsChange}
        icon={<Users className="h-4 w-4" />}
        isLoading={isLoading}
      />
      <StatCard
        title="Sessions"
        value={stats ? formatNumber(stats.sessions) : '-'}
        change={stats?.sessionsChange}
        icon={<MousePointer className="h-4 w-4" />}
        isLoading={isLoading}
      />
      <StatCard
        title="Bookings"
        value={stats ? formatNumber(stats.bookings) : '-'}
        change={stats?.bookingsChange}
        icon={<Calendar className="h-4 w-4" />}
        isLoading={isLoading}
      />
      <StatCard
        title="Revenue"
        value={stats ? formatCurrency(stats.revenue) : '-'}
        change={stats?.revenueChange}
        icon={<DollarSign className="h-4 w-4" />}
        isLoading={isLoading}
      />
    </div>
  )
}
